"use client";

import styles from "@/styles/review/index.module.scss";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState, useRef } from "react";
import { Rate, Pagination, Button, Skeleton } from "react-vant";
import Chart from "@/components/global/Chat";
import productApi from "@/service/modules/productApi";
import featuredSectionApi from "@/service/modules/featuredSectionApi";
import ProductItem from "@/components/global/ProductItem";
import { errorUtils } from "@/utils/error";
import type { ProductType } from "@/types/product";

interface GoogleReviewItem {
  id?: string | number;
  name?: string;
  star?: number;
  createTime?: string;
  review?: string;
}

export default function ReviewPage() {
  const [loading, setLoading] = useState(true);
  const [reviewSummary, setReviewSummary] = useState({
    count: 0,
    item: {
      rating1: 0,
      rating2: 0,
      rating3: 0,
      rating4: 0,
      rating5: 0,
    },
    avgScore: 0,
  });
  const [reviews, setReviews] = useState<GoogleReviewItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [goodsList, setGoodsList] = useState<ProductType[]>([]);
  const pageSize = 10;
  const reviewsListRef = useRef<HTMLDivElement>(null);

  const getReviewList = async (page: number, isInitialLoad = false): Promise<boolean> => {
    try {
      // 仅首次加载用 loading 整页骨架
      if (isInitialLoad) {
        setLoading(true);
      }
      const params = {
        start: (page - 1) * pageSize + 1,
        rows: pageSize,
      };
      const res = await productApi.getGoogleReviews(params);
      if (res?.data) {
        const {
          googleReview,
          googleReviewCount,
          reviewSummary: summary,
        } = res.data;
        setReviews(googleReview || []);
        setTotalPages(Math.ceil((googleReviewCount || 0) / pageSize));
        // 保存 reviewSummary（只在第一次加载时）
        if (summary && page === 1) {
          setReviewSummary(summary);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
      setReviews([]);
      return false;
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    const hasData = await getReviewList(page);
    if (hasData) {
      reviewsListRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const getGoodsList = async () => {
    try {
      const res = await featuredSectionApi.getNewHotList();
      const pageList = res.data;
      let list: ProductType[] = [];
      if (pageList && pageList.length) {
        list = pageList.map((v: ProductType) => {
          const obj: ProductType = {
            ...v,
            productName: v.name,
            imgUrl: v.filename ? v.filename : "",
          };
          return obj;
        });
      }
      setGoodsList(list);
    } catch (error: unknown) {
      const message = errorUtils.getErrorMessage(error);
      console.log("Failed to fetch goods list:", message);
    }
  };

  useEffect(() => {
    getReviewList(1, true);
    getGoodsList();
  }, []);
  const formatScore = (score: number) => {
    const decimal = score - Math.floor(score);
    return decimal > 0.4 ? Math.floor(score) + 0.5 : score;
  };

  return (
    <div className={styles["page-review"]}>
      {loading ? (
        <div
          className={styles["skeleton-box"]}
          style={{ minHeight: "80vh", paddingTop: "40px" }}
        >
          <Skeleton />
          <Skeleton style={{ marginTop: "28px" }} />
          <Skeleton style={{ marginTop: "28px" }} />
        </div>
      ) : (
        <div className={styles["summary-of-comments"]}>
          <div className={styles["crazy-sale-info"]}>
            <Image
              src={`${process.env.IMAGE_DOMAIN}/svg/crazysales-logo.svg`}
              alt="Crazysales Logo"
              width={80}
              height={80}
            />
            <div className={styles["info"]}>
              <h2>crazysales.com.au</h2>
              <div className={styles["info-review"]}>
                <div className={styles["point"]}>{reviewSummary.avgScore}</div>
                <div className={styles["rate"]}>
                  <Rate
                    value={formatScore(reviewSummary.avgScore)}
                    size={"16px"}
                    color={"#FBC72E"}
                    voidColor={"#dedede"}
                    allowHalf
                    readOnly
                  />
                </div>
                <div className={styles["positive"]}>
                  {`${((reviewSummary.avgScore / 5) * 100).toFixed(0)}%`}{" "}
                  positive
                </div>
              </div>
              <p>
                Ratings based on data collected by Google and/or its partners
              </p>
            </div>
          </div>

          <div className={styles["summary-data"]}>
            <div className={styles["chart-list"]}>
              <Chart
                reviewSummary={reviewSummary.item}
                reviewCount={reviewSummary.count}
              />
            </div>
            <div className={styles["summary"]}>
              <div className={styles["review-total"]}>
                {reviewSummary.count} reviews
              </div>
              <span className={styles["in-total"]}>in the past 12 months</span>
            </div>
          </div>

          <div className={styles["reviews-experience"]}>
            <div className={styles["experience-hands"]}>
              <Image
                src="https://img.crazysales.com.au/images/newhomepage/base/crazysales-reviews-hands.jpg"
                alt="Crazy Sales Reviews"
                width={160}
                height={101}
              />
            </div>
            <div className={styles["experience-text"]}>
              <h4>
                Crazy Sales Reviews gather our customers&apos; experience on
                Crazy sales.
              </h4>
            </div>
            <p className={styles["experience-detail"]}>
              Read crazy sales reviews on Crazysales.com.au. Crazy Sales Review
              shows you straight happy customer online shopping experience. Hear
              what Google Customer Reviews have said about Crazy Sales.
            </p>
          </div>

          <div ref={reviewsListRef} className={styles["reviews-list"]}>
            <h2 className={styles["list-subtitle"]}>
              {reviewSummary.count} reviews from{" "}
              <span className={styles["highlight"]}>
                Google Customer Reviews
              </span>
            </h2>
            <>
              <div className={styles["list-container"]}>
                {reviews.length > 0 ? (
                  reviews.map((item, index) => (
                    <div
                      key={item.id || `review-${currentPage}-${index}`}
                      className={styles["list-item"]}
                    >
                      <div className={styles["head"]}>
                        <div className={styles["left"]}>
                          <span className={styles["username"]}>
                            {item.name || "Anonymous"}
                          </span>
                          <Rate
                            count={5}
                            value={item.star || 0}
                            size="14px"
                            color="#FBC72E"
                            voidColor="#dedede"
                            allowHalf
                            readOnly
                          />
                          <div className={styles["google-img"]}>
                            <Image
                              src="https://img.crazysales.com.au/images/Sign-Google.png"
                              alt="Google"
                              width={20}
                              height={20}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={styles["time"]}>{item.createTime}</div>
                      <div className={styles["content"]}>
                        <p>{item.review}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles["no-data"]}>No reviews yet</div>
                )}
              </div>
              {totalPages > 1 && (
                <div className={styles["pagination-container"]}>
                  <Pagination
                    value={currentPage}
                    totalItems={totalPages * pageSize}
                    itemsPerPage={pageSize}
                    onChange={handlePageChange}
                    showPageSize={5}
                    prevText="Prev"
                    nextText="Next"
                  />
                </div>
              )}

              <div className={styles['see-more-reviews']}>
                {reviewSummary.count} reviews
                
                <Link 
                  href="https://www.google.com/shopping/customerreviews/merchantreviews?q=crazysales.com.au" 
                  target="_blank"
                  className={styles['see-more-link']}
                >
                  See more reviews
                </Link>
              </div>
            </>
          </div>

          {goodsList.length > 0 && (
            <div className={styles["goods-list"]}>
              <h3 className={styles["goods-title"]}>Hot & New</h3>
              <div className={styles["goods-container"]}>
                {goodsList.map((item, index: number) => (
                  <div
                    className={styles["goods-item"]}
                    key={item.id ? String(item.id) : `goods-${index}`}
                  >
                    <ProductItem product={item} />
                  </div>
                ))}
              </div>
              <Link href="/">
                <Button
                  round
                  type="primary"
                  style={{
                    fontWeight: "700",
                    width: "90%",
                    margin: "0 auto 20px auto",
                  }}
                  block
                >
                  View More
                </Button>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
